
// Angell: directional (sun) shadow map for point clouds.
// The visible nodes are rendered from an orthographic light camera into a float target holding linear depth
// from the light (R channel, 0 = no caster). The light frustum is fitted to the view frustum (far plane clamped
// to the cloud extent) intersected with the cloud bounds, so map resolution follows the current view. The map is
// only re-rendered when the camera, the sun or the visible node set changes. Sampling happens per vertex in
// pointcloud.vs (num_shadowmaps block) with a 16-tap Poisson PCF.

import * as THREE from "../../libs/three.js/build/three.module.js";
import {PointCloudMaterial} from "../materials/PointCloudMaterial.js";
import {PointSizeType, PointShape} from "../defines.js";

export class SunShadowMap{

	constructor(viewer, size = 2048){
		this.viewer = viewer;
		this.size = size;

		this.target = new THREE.WebGLRenderTarget(size, size, {
			minFilter: THREE.NearestFilter,
			magFilter: THREE.NearestFilter,
			format: THREE.RGBAFormat,
			type: THREE.FloatType,
			depthBuffer: true,
			stencilBuffer: false,
		});

		this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
		this.camera.up.set(0, 0, 1);

		this.materials = new Map();
		this.key = null;
		this.rendered = false;

		// scalar uniforms bound by PotreeRenderer.renderNodes for every node
		this.uniforms = {
			uShadowStrength: 0.6,
			uShadowSoft: 2.0,
			uShadowTexel: 1 / size,
			uShadowTexelWorld: 1.0,
			uShadowBiasK: 2.0,
			uShadowSlope: 1.5,
		};
	}

	static sunDirection(azimuthDeg, elevationDeg){
		const az = azimuthDeg * Math.PI / 180;
		const el = elevationDeg * Math.PI / 180;
		// azimuth clockwise from north (+y), elevation above the horizon; vector points towards the sun
		return new THREE.Vector3(Math.sin(az) * Math.cos(el), Math.cos(az) * Math.cos(el), Math.sin(el));
	}

	nodeSignature(pointclouds){
		let h = 0;
		let n = 0;
		for(const pc of pointclouds){
			for(const node of pc.visibleNodes){
				const name = node.name;
				for(let i = 0; i < name.length; i++){
					h = (h * 31 + name.charCodeAt(i)) | 0;
				}
				n++;
			}
		}
		return `${n}:${h}`;
	}

	// fit the light camera; returns false if the cloud is not in view
	fit(viewCamera, pointclouds, sun){
		const cloudBox = new THREE.Box3();
		for(const pc of pointclouds){
			cloudBox.union(pc.boundingBox.clone().applyMatrix4(pc.matrixWorld));
		}
		if(cloudBox.isEmpty()){
			return false;
		}

		const sunDir = SunShadowMap.sunDirection(sun.azimuth, sun.elevation);
		const centre = cloudBox.getCenter(new THREE.Vector3());
		const radius = cloudBox.getSize(new THREE.Vector3()).length() / 2;

		const cam = this.camera;
		cam.position.copy(centre).addScaledVector(sunDir, radius * 2 + 1);
		cam.up.set(0, 0, 1);
		if(sun.elevation > 80){
			cam.up.set(0, 1, 0);
		}
		cam.lookAt(centre);
		cam.updateMatrixWorld(true);
		cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
		const toLight = cam.matrixWorldInverse;

		// light-space AABB of the whole cloud (casters may come from anywhere along the light direction)
		const cloudL = new THREE.Box3();
		const corner = new THREE.Vector3();
		for(let i = 0; i < 8; i++){
			corner.set(
				(i & 1) ? cloudBox.max.x : cloudBox.min.x,
				(i & 2) ? cloudBox.max.y : cloudBox.min.y,
				(i & 4) ? cloudBox.max.z : cloudBox.min.z);
			cloudL.expandByPoint(corner.applyMatrix4(toLight));
		}

		// light-space AABB of the view frustum, far plane clamped to the farthest cloud corner
		let farDist = 0;
		for(let i = 0; i < 8; i++){
			corner.set(
				(i & 1) ? cloudBox.max.x : cloudBox.min.x,
				(i & 2) ? cloudBox.max.y : cloudBox.min.y,
				(i & 4) ? cloudBox.max.z : cloudBox.min.z);
			farDist = Math.max(farDist, corner.distanceTo(viewCamera.position));
		}
		farDist = Math.min(farDist, viewCamera.far);
		const viewL = new THREE.Box3();
		const projInv = viewCamera.projectionMatrix.clone().invert();
		const camPos = viewCamera.position.clone();
		const scale = viewCamera.isOrthographicCamera ? 1 : (farDist / viewCamera.far);
		for(let i = 0; i < 8; i++){
			corner.set((i & 1) ? 1 : -1, (i & 2) ? 1 : -1, (i & 4) ? 1 : -1);
			corner.applyMatrix4(projInv).applyMatrix4(viewCamera.matrixWorld);
			if(i & 4){
				corner.sub(camPos).multiplyScalar(scale).add(camPos);
			}
			viewL.expandByPoint(corner.applyMatrix4(toLight));
		}

		const region = cloudL.clone().intersect(viewL);
		if(region.isEmpty() || !isFinite(region.min.x) || !isFinite(region.max.x)){
			return false;
		}

		const pad = Math.max((region.max.x - region.min.x), (region.max.y - region.min.y)) * 0.02 + 0.5;
		cam.left = region.min.x - pad;
		cam.right = region.max.x + pad;
		cam.bottom = region.min.y - pad;
		cam.top = region.max.y + pad;
		const zPad = 1.0;
		cam.near = Math.max(0.01, -cloudL.max.z - zPad);
		cam.far = -cloudL.min.z + zPad;
		cam.updateProjectionMatrix();

		this.uniforms.uShadowTexelWorld = Math.max(cam.right - cam.left, cam.top - cam.bottom) / this.size;
		return true;
	}

	getMaterial(pointcloud){
		let mat = this.materials.get(pointcloud);
		if(!mat){
			mat = new PointCloudMaterial();
			mat.activeAttributeName = 'depth';
			mat.pointSizeType = PointSizeType.ADAPTIVE;
			mat.shape = PointShape.CIRCLE;
			mat.size = 2.0;
			mat.minSize = 1.0;
			mat.maxSize = 64.0;
			mat.useEDL = false;
			mat.weighted = false;
			this.materials.set(pointcloud, mat);
		}
		return mat;
	}

	renderMap(pointclouds){
		const viewer = this.viewer;
		const renderer = viewer.renderer;
		const originals = new Map();

		for(const pc of pointclouds){
			const src = pc.material;
			const mat = this.getMaterial(pc);
			mat.visibleNodesTexture = src.visibleNodesTexture;
			mat.uniforms.visibleNodes.value = src.visibleNodesTexture;
			mat.uniforms.octreeSize.value = pc.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
			mat.spacing = pc.pcoGeometry.spacing;
			mat.screenWidth = this.size;
			mat.screenHeight = this.size;
			mat.classification = src.classification;
			mat.uniforms.classificationLUT.value.image.data = src.uniforms.classificationLUT.value.image.data;
			mat.classificationTexture.needsUpdate = true;
			mat.uniforms.uFilterReturnNumberRange.value = src.uniforms.uFilterReturnNumberRange.value;
			mat.uniforms.uFilterNumberOfReturnsRange.value = src.uniforms.uFilterNumberOfReturnsRange.value;
			mat.uniforms.uFilterGPSTimeClipRange.value = src.uniforms.uFilterGPSTimeClipRange.value;
			mat.uniforms.uFilterPointSourceIDClipRange.value = src.uniforms.uFilterPointSourceIDClipRange.value;
			mat.clipTask = src.clipTask;
			mat.clipMethod = src.clipMethod;
			mat.setClipBoxes(src.clipBoxes);
			mat.setClipPolygons(src.clipPolygons);
			originals.set(pc, src);
			pc.material = mat;
		}

		const oldTarget = renderer.getRenderTarget();
		const oldClear = renderer.getClearColor(new THREE.Color());
		const oldAlpha = renderer.getClearAlpha();

		renderer.setRenderTarget(this.target);
		renderer.setClearColor(0x000000, 1);
		renderer.clear(true, true, false);
		viewer.pRenderer.render(viewer.scene.scenePointCloud, this.camera, this.target, {});

		renderer.setRenderTarget(oldTarget);
		renderer.setClearColor(oldClear, oldAlpha);

		for(const [pc, mat] of originals){
			pc.material = mat;
		}
	}

	// returns true when a usable map exists for this frame
	update(viewCamera, pointclouds, sun){
		const strength = Math.max(0, Math.min(1, sun.strength));
		this.uniforms.uShadowStrength = strength;
		this.uniforms.uShadowSoft = 0.5 + 5.0 * Math.max(0, Math.min(1, sun.softness));
		const grazing = Math.tan((90 - Math.max(5, Math.min(85, sun.elevation))) * Math.PI / 180);
		this.uniforms.uShadowSlope = Math.max(1.5, Math.min(4.0, grazing));

		const m = viewCamera.matrixWorld.elements;
		const p = viewCamera.projectionMatrix.elements;
		const key = [
			m.map(v => v.toFixed(3)).join(','),
			p.map(v => v.toFixed(4)).join(','),
			sun.azimuth, sun.elevation,
			this.nodeSignature(pointclouds),
		].join('|');

		if(key === this.key && this.rendered){
			return true;
		}

		if(!this.fit(viewCamera, pointclouds, sun)){
			this.rendered = false;
			this.key = null;
			return false;
		}

		this.renderMap(pointclouds);
		this.key = key;
		this.rendered = true;
		return true;
	}
}
